import { useState } from "react";
import type { Section } from "../api/types";
import { useT } from "../i18n";
import { addSection, deleteSection, updateSection } from "../sync/actions";
import { IconChevronDown, IconChevronRight, IconEdit, IconMore, IconTrash } from "../ui/icons";
import { Confirm, Menu, useAnchor } from "../ui/primitives";

export function SectionHeader({
  section,
  count,
  title,
  extra,
  collapsed,
  onToggle,
}: {
  section?: Section;
  count: number;
  title: string;
  extra?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const t = useT();
  const menu = useAnchor();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(title);
  const [confirm, setConfirm] = useState(false);
  if (editing && section) {
    return (
      <form
        className="editor-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void updateSection(section.id, { name: name.trim() });
          setEditing(false);
        }}
      >
        <input
          className="input"
          style={{ border: "none", fontWeight: 700 }}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="editor-footer" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn btn-primary">
            {t("save")}
          </button>
        </div>
      </form>
    );
  }
  return (
    <div className="section-header" data-testid="section-header" data-section-name={title}>
      {onToggle ? (
        <button
          type="button"
          className="tree-toggle"
          onClick={onToggle}
          aria-label={collapsed ? t("expand") : t("collapse")}
        >
          {collapsed ? <IconChevronRight size={16} /> : <IconChevronDown size={16} />}
        </button>
      ) : (
        <span style={{ width: 20 }} />
      )}
      <div className="title">
        <span>{title}</span>
        <span className="count">{count}</span>
      </div>
      {extra}
      {section && (
        <span className="actions">
          <button type="button" className="icon-btn" onClick={menu.open} aria-label={t("more")}>
            <IconMore size={18} />
          </button>
        </span>
      )}
      {menu.anchor && section && (
        <Menu
          anchor={menu.anchor}
          onClose={menu.close}
          placement="bottom-end"
          items={[
            { key: "rename", label: t("rename"), icon: <IconEdit size={18} />, onSelect: () => setEditing(true) },
            { key: "sep", label: "", separator: true },
            {
              key: "delete",
              label: t("delete"),
              icon: <IconTrash size={18} />,
              danger: true,
              onSelect: () => setConfirm(true),
            },
          ]}
        />
      )}
      {confirm && section && (
        <Confirm
          message={t("deleteConfirm", { name: section.name })}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={() => void deleteSection(section.id)}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}

export function SectionCollapse(section: Section) {
  return () => void updateSection(section.id, { is_collapsed: !section.is_collapsed });
}

export function AddSection({
  projectId,
  orderKey,
  always,
}: {
  projectId: string;
  orderKey?: string;
  always?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  if (!open) {
    return (
      <button
        type="button"
        className={`add-section-divider${always ? " always" : ""}`}
        onClick={() => setOpen(true)}
        data-testid="add-section"
      >
        {t("addSection")}
      </button>
    );
  }
  return (
    <form
      className="editor-card"
      style={{ marginTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        addSection(projectId, name.trim(), orderKey);
        setName("");
        setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <input
        className="input"
        style={{ border: "none", fontWeight: 700, padding: 0 }}
        autoFocus
        placeholder={t("sectionName")}
        value={name}
        data-testid="section-name-input"
        onChange={(e) => setName(e.target.value)}
      />
      <div className="editor-footer" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
          {t("cancel")}
        </button>
        <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
          {t("addSection")}
        </button>
      </div>
    </form>
  );
}
